import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bitcoin, Loader2, Trash2, TrendingUp, TrendingDown, Sparkles, Mail, Check, AlertTriangle, MessageCircle, ShieldCheck, KeyRound, ExternalLink, Trophy, Plus, Wallet, RefreshCw, Lock, Link as LinkIcon } from "lucide-react";
import { cryptoRun, cryptoList, cryptoDelete, messariSettingsGet, messariSettingsSave, scrapeSourceAdd, scrapeSourceList, scrapeSourceDelete, invoStatus, invoOtpStart, invoOtpVerify, invoMoves, invoDisconnect } from "../lib/api";
import { toast } from "sonner";

const IdeaCard = ({ idea, n }) => {
  const long = (idea.direction || "").toLowerCase() === "long";
  return (
    <div className="bg-[#0a1020] border border-[#7c3aed33] rounded-xl p-3.5 space-y-2" data-testid="crypto-idea-card">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-cyan-50 font-bold text-sm flex items-center gap-2">
          <span className="text-purple-300/50">{n}.</span>
          <span className="font-mono text-purple-300 text-base">{idea.ticker}</span>
          <span className="text-cyan-200/70 font-normal">{idea.company}</span>
        </h4>
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold border flex items-center gap-1 ${long ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-rose-300 border-rose-500/40 bg-rose-500/10"}`}>
          {long ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{idea.direction} · {idea.conviction}
        </span>
      </div>
      <p className="text-cyan-100/90 text-sm leading-relaxed">{idea.thesis}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="bg-[#7c3aed11] rounded-lg px-2 py-1.5"><p className="text-purple-300/60">Entry</p><p className="text-cyan-50 font-semibold">{idea.entry}</p></div>
        <div className="bg-emerald-500/8 rounded-lg px-2 py-1.5"><p className="text-emerald-300/60">Target</p><p className="text-emerald-200 font-semibold">{idea.target}</p></div>
        <div className="bg-rose-500/8 rounded-lg px-2 py-1.5"><p className="text-rose-300/60">Stop</p><p className="text-rose-200 font-semibold">{idea.stop}</p></div>
        <div className="bg-[#7c3aed11] rounded-lg px-2 py-1.5"><p className="text-purple-300/60">Timeframe</p><p className="text-cyan-50 font-semibold">{idea.timeframe}</p></div>
      </div>
      <div className="space-y-1 text-xs">
        {idea.catalyst && <p className="text-cyan-200/80"><span className="text-purple-300 font-semibold">Catalyst:</span> {idea.catalyst}</p>}
        {idea.fundamentals && <p className="text-cyan-200/70"><span className="text-cyan-300/60 font-semibold">On-chain:</span> {idea.fundamentals}</p>}
        {idea.technicals && <p className="text-cyan-200/70"><span className="text-cyan-300/60 font-semibold">Technicals:</span> {idea.technicals}</p>}
        {idea.sentiment && <p className="text-cyan-200/70"><span className="text-cyan-300/60 font-semibold">Sentiment:</span> {idea.sentiment}</p>}
      </div>
      {idea.jev && (
        <div className="mt-1 rounded-lg border border-[#7c3aed44] bg-[#7c3aed11] px-2.5 py-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid="crypto-jev-verdict">
          <span className="text-purple-300 font-bold uppercase tracking-wider flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Jev</span>
          {idea.jev.action && (
            <span className={`px-2 py-0.5 rounded-full font-bold border capitalize ${(idea.jev.action === "buy") ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : (idea.jev.action === "sell") ? "text-rose-300 border-rose-500/40 bg-rose-500/10" : "text-amber-300 border-amber-500/40 bg-amber-500/10"}`}>
              {idea.jev.action}{idea.jev.action_confidence != null ? ` · ${Math.round(idea.jev.action_confidence * 100)}%` : ""}
            </span>
          )}
          {idea.jev.conviction_level && <span className="text-cyan-100/80">{idea.jev.conviction_level}</span>}
        </div>
      )}
      {idea.sources?.length > 0 && <p className="text-purple-300/40 text-[11px] truncate">Sources: {idea.sources.slice(0, 4).join(", ")}</p>}
    </div>
  );
};

const BriefCard = ({ r, onDelete }) => {
  if (r.status === "processing") {
    return (
      <div className="bg-[#060b18] border border-[#7c3aed33] rounded-2xl p-4 flex items-center gap-3" data-testid="crypto-processing">
        <Loader2 className="w-5 h-5 animate-spin text-purple-300 shrink-0" />
        <div>
          <p className="text-cyan-100 text-sm font-semibold">Nexus is scanning the crypto markets…</p>
          <p className="text-purple-300/40 text-xs">Live prices, narratives, news & macro — ideas coming.</p>
        </div>
      </div>
    );
  }
  if (r.status === "error") {
    return (
      <div className="bg-rose-500/5 border border-rose-500/30 rounded-2xl p-4 flex items-start gap-3" data-testid="crypto-error">
        <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
        <div className="flex-1"><p className="text-rose-200 text-sm font-semibold">Couldn't build the brief</p><p className="text-rose-300/70 text-xs mt-0.5">{r.error || "Try again shortly."}</p></div>
        {onDelete && <button onClick={() => onDelete(r.id)} className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>}
      </div>
    );
  }
  return (
    <div className="bg-[#060b18] border border-[#7c3aed33] rounded-2xl p-4 space-y-3" data-testid="crypto-brief-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-purple-300 font-bold text-base">₿ Crypto Trade Brief · {r.date}</h3>
          <div className="flex items-center gap-2 mt-1">
            {r.delivered_email && <span className="text-emerald-300/80 text-[11px] flex items-center gap-1"><Mail className="w-3 h-3" /> emailed</span>}
            {r.delivered_wa && <span className="text-emerald-300/80 text-[11px] flex items-center gap-1"><MessageCircle className="w-3 h-3" /> WhatsApp</span>}
            {r.manual && <span className="text-purple-300/40 text-[11px]">manual run</span>}
          </div>
        </div>
        {onDelete && <button onClick={() => onDelete(r.id)} data-testid="crypto-delete-btn" className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>}
      </div>
      {r.market_summary && <p className="text-cyan-100/85 text-sm leading-relaxed">{r.market_summary}</p>}
      <div className="space-y-2.5">{(r.ideas || []).map((idea, i) => <IdeaCard key={i} idea={idea} n={i + 1} />)}</div>
      {r.disclaimer && <p className="text-purple-300/40 text-[11px] italic">{r.disclaimer}</p>}
    </div>
  );
};

const MessariSetup = ({ userId, cfg, onSaved }) => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!key.trim()) return;
    setBusy(true);
    try { const r = await messariSettingsSave(userId, key.trim()); setKey(""); onSaved(r); toast.success("Messari key saved — crypto narratives enriched"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save key"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-[#060b18] border border-[#7c3aed22] rounded-xl p-3 space-y-2" data-testid="messari-key-setup">
      <div className="flex items-center gap-2 flex-wrap">
        <KeyRound className="w-4 h-4 text-purple-300/60" />
        {cfg?.configured
          ? <span className="text-cyan-100 text-sm flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-300" /> Messari connected <span className="text-purple-300/40 font-mono text-xs">{cfg.masked}</span></span>
          : <span className="text-amber-200/80 text-xs">Add your free Messari key to layer in crypto sector/narrative context (prices work without it).</span>}
        <a href="https://messari.io/account/api" target="_blank" rel="noreferrer" className="ml-auto text-purple-300 text-[11px] flex items-center gap-1 hover:underline">get a key <ExternalLink className="w-3 h-3" /></a>
      </div>
      <div className="flex items-center gap-2">
        <input value={key} onChange={(e) => setKey(e.target.value)} type="password" placeholder={cfg?.configured ? "replace key" : "paste your Messari key"} data-testid="messari-key-input"
          className="flex-1 bg-[#0a1020] border border-[#7c3aed22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm" />
        <button onClick={save} disabled={busy} data-testid="messari-key-save" className="px-3 py-1.5 rounded-lg text-xs font-bold text-purple-300 border border-[#7c3aed66] hover:bg-[#7c3aed11] disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
};

const LeaderboardBox = ({ userId }) => {
  const [boards, setBoards] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(async () => {
    try { const r = await scrapeSourceList(userId); setBoards((r.items || []).filter((x) => x.kind === "leaderboard")); } catch (e) {}
  }, [userId]);
  useEffect(() => { refresh(); }, [refresh]);
  const add = async () => {
    if (!url.trim()) { toast.error("Paste a leaderboard URL"); return; }
    setBusy(true);
    try { const r = await scrapeSourceAdd(userId, url.trim(), "", "leaderboard"); setBoards((h) => [r, ...h]); setUrl(""); toast.success("Pinned — Nexus will mine these traders every brief"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't pin"); }
    finally { setBusy(false); }
  };
  const del = async (id) => { try { await scrapeSourceDelete(userId, id); setBoards((h) => h.filter((x) => x.id !== id)); } catch (e) {} };
  return (
    <div className="bg-[#060b18] border border-[#f59e0b22] rounded-xl p-3 space-y-2" data-testid="crypto-leaderboards">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-amber-300/70" />
        <p className="text-amber-200/90 text-sm font-semibold">Top-trader leaderboards</p>
        <span className="text-amber-200/40 text-xs">Nexus mines these for idea candidates</span>
      </div>
      <p className="text-cyan-200/50 text-xs">Paste any public crypto-trader leaderboard / profile (e.g. a Hyperliquid or copy-trade leaderboard). Nexus scrapes the sharpest traders' recent moves into your briefs.</p>
      <div className="flex items-center gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… leaderboard URL" data-testid="crypto-leaderboard-input"
          className="flex-1 bg-[#0a1020] border border-[#f59e0b22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm outline-none" />
        <button onClick={add} disabled={busy} data-testid="crypto-leaderboard-add" className="px-3 py-1.5 rounded-lg text-xs font-bold text-amber-200 border border-[#f59e0b55] hover:bg-[#f59e0b11] disabled:opacity-50 flex items-center gap-1">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Pin</button>
      </div>
      {boards.map((b) => (
        <div key={b.id} className="flex items-center gap-2 text-xs" data-testid="crypto-leaderboard-item">
          <Trophy className="w-3.5 h-3.5 text-amber-300/50 shrink-0" />
          <span className="text-cyan-100/80 truncate flex-1">{b.url}</span>
          <button onClick={() => del(b.id)} className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
};

const InvoBox = ({ userId }) => {
  const [st, setSt] = useState(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("email"); // email | code
  const [busy, setBusy] = useState(false);
  const [moves, setMoves] = useState("");
  const [loadingMoves, setLoadingMoves] = useState(false);

  const load = useCallback(async () => {
    try { const r = await invoStatus(userId); setSt(r); return r; } catch (e) { return null; }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const sendCode = async () => {
    if (!email.trim()) { toast.error("Enter your Invo email"); return; }
    setBusy(true);
    try { await invoOtpStart(userId, email.trim()); setStage("code"); toast.success("Code sent — check your email (and spam)"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send code"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code.trim()) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try { await invoOtpVerify(userId, email.trim(), code.trim()); setCode(""); setStage("email"); toast.success("Invo connected ✓ feeding the Trading House"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Code rejected — request a fresh one"); }
    finally { setBusy(false); }
  };

  const showMoves = async () => {
    setLoadingMoves(true);
    try { const r = await invoMoves(userId); setMoves(r.dossier || "No moves available right now."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load moves"); }
    finally { setLoadingMoves(false); }
  };

  const disconnect = async () => { try { await invoDisconnect(userId); setMoves(""); toast.success("Invo disconnected"); await load(); } catch (e) {} };

  const connected = st?.token_connected;
  return (
    <div className="bg-[#060b18] border border-[#22d3ee22] rounded-xl p-3 space-y-2.5" data-testid="crypto-invo-box">
      <div className="flex items-center gap-2 flex-wrap">
        <Wallet className="w-4 h-4 text-cyan-300/70" />
        <p className="text-cyan-100 text-sm font-semibold">Invo — top traders' moves</p>
        {connected
          ? <span className="text-emerald-300/80 text-[11px] flex items-center gap-1"><Check className="w-3 h-3" /> {st.username || st.email}</span>
          : <span className="text-amber-200/70 text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> connect with your email code</span>}
        {connected && <button onClick={disconnect} data-testid="invo-disconnect" className="ml-auto text-cyan-300/40 hover:text-rose-300 text-[11px]">disconnect</button>}
      </div>

      {!connected ? (
        <div className="space-y-2">
          <p className="text-cyan-200/50 text-xs">Connect Invo so the A V Trading House mines the 🔥 Moves + Trending feed — top traders' live positions (win-rate, P&L, entry/target/stop) become trade signals. You enter an email code once; the session stays alive automatically.</p>
          {stage === "email" ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Invo email" data-testid="invo-email-input"
                className="flex-1 bg-[#0a1020] border border-[#22d3ee22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm outline-none" />
              <button onClick={sendCode} disabled={busy} data-testid="invo-sendcode-btn" className="px-3 py-1.5 rounded-lg text-xs font-bold text-cyan-200 border border-[#22d3ee55] hover:bg-[#22d3ee11] disabled:opacity-50 flex items-center gap-1 justify-center">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LinkIcon className="w-3 h-3" />} Send code</button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code from email" inputMode="numeric" data-testid="invo-code-input"
                className="flex-1 bg-[#0a1020] border border-[#22d3ee22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm outline-none tracking-widest" />
              <button onClick={verify} disabled={busy} data-testid="invo-verify-btn" className="px-3 py-1.5 rounded-lg text-xs font-bold text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/10 disabled:opacity-50 flex items-center gap-1 justify-center">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Verify</button>
              <button onClick={sendCode} disabled={busy} className="px-2 py-1.5 rounded-lg text-[11px] text-cyan-300/50 hover:text-cyan-200">resend</button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={showMoves} disabled={loadingMoves} data-testid="invo-moves-btn" className="px-3 py-1.5 rounded-lg text-xs font-bold text-cyan-200 border border-[#22d3ee55] hover:bg-[#22d3ee11] disabled:opacity-50 flex items-center gap-1">
              {loadingMoves ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading…</> : <><RefreshCw className="w-3 h-3" /> Show live moves</>}
            </button>
            <span className="text-cyan-300/40 text-[11px]">auto-fed into every House run</span>
          </div>
          {moves && (
            <pre className="bg-[#0a1020] border border-[#22d3ee14] rounded-lg p-2.5 text-[11px] text-cyan-100/90 whitespace-pre-wrap max-h-72 overflow-auto" data-testid="invo-moves-text">{moves}</pre>
          )}
        </div>
      )}
    </div>
  );
};

export default function NexusCrypto({ session }) {
  const userId = session.user_id;
  const [items, setItems] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await cryptoList(userId);
      setItems(r.items || []);
      setCfg({ configured: r.messari_configured });
      return r.items || [];
    } catch (e) { return []; }
  }, [userId]);
  useEffect(() => { load(); (async () => { try { setCfg(await messariSettingsGet(userId)); } catch (e) {} })(); }, [load, userId]);

  useEffect(() => {
    const proc = items.some((x) => x.status === "processing");
    if (proc && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const list = await load();
        if (!list.some((x) => x.status === "processing")) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 6000);
    }
    return () => { if (pollRef.current && !items.some((x) => x.status === "processing")) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [items, load]);

  const run = async () => {
    setBusy(true);
    try { await cryptoRun(userId); toast.success("Nexus is researching crypto — your ideas will appear shortly"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start research"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { try { await cryptoDelete(userId, id); setItems((h) => h.filter((x) => x.id !== id)); } catch (e) {} };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="crypto-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-purple-300 font-bold text-2xl flex items-center gap-2"><Bitcoin className="w-6 h-6" /> Crypto Ideas</h2>
            <p className="text-cyan-200/60 text-sm mt-1">Nexus hunts the crypto markets <b>twice a day</b> — live prices (CoinGecko), narratives (Messari) & macro — and brings you <b>5 high-conviction ideas</b> with entry/target/stop, here + by email & WhatsApp.</p>
          </div>
          <button onClick={run} disabled={busy} data-testid="crypto-run-btn"
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-50 flex items-center gap-2 shrink-0">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <><Sparkles className="w-4 h-4" /> Run now</>}
          </button>
        </div>

        <MessariSetup userId={userId} cfg={cfg} onSaved={(r) => setCfg(r)} />
        <InvoBox userId={userId} />
        <LeaderboardBox userId={userId} />

        {items.length > 0 ? (
          <div className="space-y-4">{items.map((r) => <BriefCard key={r.id} r={r} onDelete={del} />)}</div>
        ) : (
          <div className="text-center py-16 text-cyan-200/40" data-testid="crypto-empty">
            <Bitcoin className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No crypto briefs yet. Tap <b>Run now</b> for your first 5 ideas, or wait for the 7am auto-brief.</p>
          </div>
        )}
      </div>
    </div>
  );
}
